import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { usePoll, fmt, api, errMsg } from "../lib/api";
import { useData } from "../lib/DataContext";
import { PageHeader, Panel, TierBadge } from "../components/shared";

const TYPES = ["", "production", "consumption", "shipment", "receive", "adjustment", "initial"];
const MANUAL = new Set(["adjustment", "shipment", "receive", "initial"]);
const COLORS = { production: "text-[#10B981]", consumption: "text-[#F59E0B]", shipment: "text-[#FF5722]", receive: "text-[#38BDF8]", adjustment: "text-[#A5B4FC]", initial: "text-[#9CA3AF]" };

export default function AuditLog() {
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const { refreshAll } = useData();
  const { data, refresh } = usePoll(`/transactions?limit=500${type ? `&type=${type}` : ""}`);
  const del = async (t) => {
    if (!window.confirm(`Delete this ${t.type} line for ${t.item_name}? Stock will be corrected by ${t.qty_change > 0 ? "-" : "+"}${fmt(Math.abs(t.qty_change))} ${t.unit}.`)) return;
    try { await api.delete(`/transactions/${t.id}`); toast.success("Line deleted and stock corrected"); refresh(); refreshAll(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const rows = (data || []).filter((t) => !q || `${t.item_name} ${t.sku} ${t.note}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader kicker="// ADMIN · TRANSACTIONS" title="Audit Log" />
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <input data-testid="log-search-input" placeholder="Search item, SKU, note…" value={q} onChange={(e) => setQ(e.target.value)} className="h-12 px-4 flex-1 bg-[#0F1623] border border-[#1E293B] rounded-md focus:outline-none focus:border-[#F59E0B]" />
        <div className="flex flex-wrap gap-2">
          {TYPES.map((t) => <button key={t || "all"} data-testid={`log-type-${t || "all"}`} onClick={() => setType(t)} className={`h-12 px-3 rounded-md font-mono text-xs uppercase border ${type === t ? "bg-white text-[#090D16]" : "border-[#334155] text-[#9CA3AF]"}`}>{t || "all"}</button>)}
        </div>
      </div>
      <Panel className="overflow-auto max-h-[70vh]" data-testid="log-table">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="sticky top-0 bg-[#0B111D]"><tr className="text-left font-mono text-[10px] tracking-[0.15em] text-[#6B7280] border-b border-[#1E293B]">
            {["TIMESTAMP", "TYPE", "ITEM", "CHANGE", "BALANCE", "NOTE", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-[#1E293B]">
            {rows.map((t) => (
              <tr key={t.id} data-testid={`log-row-${t.id}`} className="hover:bg-[#172033]">
                <td className="px-4 py-3 font-mono text-xs text-[#9CA3AF] whitespace-nowrap">{new Date(t.timestamp).toLocaleString()}</td>
                <td className={`px-4 font-mono text-xs uppercase font-bold ${COLORS[t.type] || ""}`}>{t.type}</td>
                <td className="px-4"><div className="flex items-center gap-2"><TierBadge tier={t.tier} /><span>{t.item_name}</span></div></td>
                <td className={`px-4 font-mono font-bold ${t.qty_change < 0 ? "text-[#EF4444]" : "text-[#10B981]"}`}>{t.qty_change > 0 ? "+" : ""}{fmt(t.qty_change)} <span className="text-[#6B7280] text-xs">{t.unit}</span></td>
                <td className="px-4 font-mono">{fmt(t.balance_after)}</td>
                <td className="px-4 text-[#9CA3AF] text-xs max-w-xs truncate">{t.note}</td>
                <td className="px-2 w-12">{MANUAL.has(t.type) && !t.ref && (
                  <button data-testid={`log-delete-${t.id}`} onClick={() => del(t)} title="Delete line and undo its stock change"
                    className="h-9 w-9 grid place-items-center rounded-md text-[#6B7280] hover:text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 className="w-4 h-4" /></button>)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="px-4 py-6 text-[#6B7280] font-mono">NO TRANSACTIONS</td></tr>}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}
